import React from 'react';
import { Box, Grid } from '@material-ui/core';
import { AccordionSummary } from '@material-ui/core';
import { injectIntl, IntlShape } from 'react-intl';
import { get } from 'lodash';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import moment from 'moment';
import StatusChip from 'components/StatusChip';
import {
  StyledCaption,
  StyledShortBody,
  StyledLink,
} from 'components/AccordionSinistre/styles';
import { ShortBody } from 'components/Typography';
import { AssuranceIcon } from 'images';
import {
  StyledImg,
  StyledOrangeButton,
  StyledAccordionDetails,
} from 'containers/AutoDetailsPage/SuivreSinistreTab/Sinistres/styles';
import messages from 'components/AccordionSinistre/messages';
import { translate } from 'helpers/intl';
import { StyledValidAccordion } from 'containers/AutoDetailsPage/SuivreSinistreTab/Sinistres/styles';
import VerticalLinearStepper from 'components/VerticalStepper';
import EmptyJournalSinistre from 'components/AccordionSinistre/EmptyJournalSinistre';
import { useSelector } from 'react-redux';
import { isReverse } from 'helpers/intl';
import { getLocaleSelector } from 'store/LanguageProvider/selectors';
import GaClickHOC from 'helpers/GATracking/GaClickHOC';
import { EVENT_CATEGORIES } from 'helpers/GATracking';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

interface AccordionSinistreType {
  intl: IntlShape;
  getSinistreDetails: Function;
  sinistres: {
    claimNumber: string;
    claimLossDate: string;
    lastUpdateDate: string;
    vehicleReg: string;
    marque: string;
    claimStatus: string;
  }[];
  sinistreDetails: {
    title: string;
    subtitle: string;
    actualDate: any;
    timelineState: string;
    shareName: string;
    status: string;
    description: string;
  }[];
  expanded: boolean | String;
  handleChange: Function;
  listIndex: Number;
  isLoading: boolean;
}

const REACLAMATION_LINK =
  'https://www.sahamassurance.ma/contact?subject=reclamation';

const AccordionSinistre = ({
  sinistres,
  getSinistreDetails,
  listIndex,
  handleChange,
  expanded,
  sinistreDetails,
  isLoading,
  ...props
}: AccordionSinistreType) => {
  const lang = useSelector(getLocaleSelector);
  const reverse = isReverse(lang);
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('xs'));
  const direction = matches ? 'column-reverse' : 'row';
  return (
    <>
      {sinistres.map((item: any, index: Number) => (
        <StyledValidAccordion
          color={item.claimStatus === 'Terminé' ? '#6fad37' : '#fcb718'}
          aria-controls={`panel-content-${index}`}
          id={`panel-header-${index}`}
          data-testid={`sinistre-item-${index}`}
          onChange={handleChange(
            `sinistre-${listIndex}-item-${index}`,
            item.claimNumber,
          )}
          expanded={expanded === `sinistre-${listIndex}-item-${index}`}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Box width="100%">
              <Grid container justify="space-between">
                <Grid item xs={12} sm={3}>
                  <Grid item>
                    <StyledCaption>
                      {translate(get(messages, 'numSinistre'), props)}
                    </StyledCaption>
                  </Grid>
                  <Grid item>
                    <Box marginBottom={0}>
                      <StyledShortBody>{item.claimNumber}</StyledShortBody>
                    </Box>
                  </Grid>
                </Grid>
                <Grid item xs={6} sm>
                  <Grid container direction="column">
                    <Grid item>
                      <StyledCaption>
                        {translate(get(messages, 'modele'), props)}
                      </StyledCaption>
                    </Grid>
                    <Grid item>
                      <ShortBody>{item.marque}</ShortBody>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={6} sm>
                  <Grid container direction="column">
                    <Grid item>
                      <StyledCaption>
                        {translate(get(messages, 'immatriculation'), props)}
                      </StyledCaption>
                    </Grid>
                    <Grid item>
                      <ShortBody>{item.vehicleReg}</ShortBody>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={6} sm>
                  <Grid container direction="column">
                    <Grid item>
                      <StyledCaption>
                        {translate(get(messages, 'dateDeclaration'), props)}
                      </StyledCaption>
                    </Grid>
                    <Grid item>
                      <ShortBody>
                        {moment(
                          item.claimLossDate,
                          'DD/MM/YYYY',
                          true,
                        ).isValid()
                          ? item.claimLossDate
                          : moment(item.claimLossDate).format('DD/MM/YYYY')}
                      </ShortBody>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={6} sm>
                  <Grid container direction="column">
                    <Grid item>
                      <StyledCaption>
                        {translate(get(messages, 'dateMAJ'), props)}
                      </StyledCaption>
                    </Grid>
                    <Grid item>
                      <Grid item>
                        <ShortBody>
                          {moment(
                            item.lastUpdateDate,
                            'DD/MM/YYYY',
                            true,
                          ).isValid()
                            ? item.lastUpdateDate
                            : moment(item.lastUpdateDate).format('DD/MM/YYYY')}
                        </ShortBody>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={6} sm>
                  <Grid container direction="column">
                    <Grid item>
                      <StyledCaption>
                        {translate(get(messages, 'status'), props)}
                      </StyledCaption>
                    </Grid>
                    <Grid item>
                      <Box marginBottom={0}>
                        <StatusChip
                          status={
                            item.claimStatus === 'Terminé'
                              ? 'success'
                              : item.claimStatus === 'Annulé'
                              ? 'cancel'
                              : 'warning'
                          }
                          label={item.claimStatus}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </AccordionSummary>

          <StyledAccordionDetails>
            {isLoading ? (
              <></>
            ) : sinistreDetails && sinistreDetails.length > 0 ? (
              <Grid
                container
                spacing={2}
                justify="space-between"
                alignItems="flex-start"
              >
                <Grid item xs={12} sm={3}>
                  <ShortBody weight="500" color="#838EA4">
                    {translate(get(messages, 'sinistre_journale'), props)}
                  </ShortBody>
                </Grid>
                <Box mt={matches ? 0 : -6}>
                  <Grid container direction={direction} justify="flex-end">
                    <Grid item xs={12} sm={3}>
                      <Box pb={3}>
                        <StyledLink href={REACLAMATION_LINK} target="_blank">
                          <GaClickHOC
                            category={EVENT_CATEGORIES.AUTO_DETAILS}
                            label="Suivi de mes sinistresFaire une réclamation"
                          >
                            <StyledOrangeButton variant="contained">
                              <StyledImg
                                reverse={reverse}
                                src={AssuranceIcon.survey}
                                alt="survey-icon"
                              />
                              {translate(
                                get(messages, 'faireReclamation'),
                                props,
                              )}
                            </StyledOrangeButton>
                          </GaClickHOC>
                        </StyledLink>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <VerticalLinearStepper
                        sinistreDetails={sinistreDetails}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ) : (
              <EmptyJournalSinistre />
            )}
          </StyledAccordionDetails>
        </StyledValidAccordion>
      ))}
    </>
  );
};

export default injectIntl(AccordionSinistre);
